import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldPlus, ChatCircleDots, Buildings, ArrowRight, DeviceMobile, EnvelopeSimple, Key, IdentificationCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Logo, Copyright } from "@/components/insurehub/Logo";

export default function Login() {
  const { login, register, setSession } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [method, setMethod] = useState("email"); // email | mobile | otp
  const [mobile, setMobile] = useState("");
  const [otpStep, setOtpStep] = useState("request"); // request | verify
  const [otpCode, setOtpCode] = useState("");

  const go = (user) => navigate(user.role === "admin" || user.role === "master_admin" ? "/admin" : "/chat");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let user;
      if (method === "mobile") {
        const { data } = await api.post("/auth/login", { mobile: mobile.trim(), password });
        user = setSession(data.token, data.user);
      } else {
        user = await login(email.trim(), password);
      }
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      go(user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (otpStep === "request") {
        const { data } = await api.post("/auth/otp/request", { email: email.trim(), purpose: "email_login" });
        if (data.demo_code) { toast.info(`Demo mode: your code is ${data.demo_code}`, { duration: 8000 }); setOtpCode(data.demo_code); }
        else toast.success("Verification code emailed.");
        setOtpStep("verify");
      } else {
        const { data } = await api.post("/auth/otp/verify", { email: email.trim(), code: otpCode.trim(), purpose: "email_login" });
        const user = setSession(data.token, data.user);
        toast.success(`Welcome, ${user.name.split(" ")[0]}!`);
        go(user);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ name: name.trim(), email: email.trim(), password, mobile: mobile.trim() });
      toast.success("Account created. Welcome to Aarogya Assist Chatbot!");
      go(user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fill = (em, pw) => {
    setMethod("email");
    setEmail(em);
    setPassword(pw);
    setTab("login");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-emerald-950 text-white p-12 overflow-hidden grain">
        <div className="relative z-10">
          <Logo size={44} textClass="text-white text-xl" />
        </div>

        <div className="relative z-10 max-w-md">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4">
            Group Medical Insurance
          </p>
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">
            Your health cover, one chat away.
          </h1>
          <p className="mt-6 text-emerald-100/70 text-base leading-relaxed">
            Check coverage, download your e-card, file & track claims and find network
            hospitals — all through a friendly WhatsApp-style assistant for your company.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: ChatCircleDots, text: "AI assistant answers your policy questions instantly" },
              { icon: ShieldPlus, text: "Digital e-health card always in your pocket" },
              { icon: Buildings, text: "Corporate admin dashboard for HR teams" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <f.icon size={18} className="text-emerald-300" />
                </div>
                <span className="text-sm text-emerald-50/90">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-xs text-emerald-200/50">
          Our team will call you back · © {new Date().getFullYear()} Aarogya Innovate Pvt Ltd
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#FAFAFA]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8">
            <Logo size={40} textClass="text-lg" />
          </div>

          <h2 className="font-heading text-3xl font-semibold tracking-tight text-emerald-950">
            {tab === "login" ? "Sign in" : "Create account"}
          </h2>
          <p className="text-sm text-zinc-500 mt-2 mb-8">
            {tab === "login"
              ? "Access your group medical insurance assistant."
              : "Join your company's health cover in seconds."}
          </p>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-6" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <div className="flex gap-2 mb-5">
                {[
                  { k: "email", label: "Email", icon: EnvelopeSimple },
                  { k: "mobile", label: "Mobile", icon: DeviceMobile },
                  { k: "otp", label: "Email OTP", icon: Key },
                ].map((m) => (
                  <button key={m.k} type="button" onClick={() => { setMethod(m.k); setOtpStep("request"); }}
                    data-testid={`method-${m.k}`}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-medium transition-colors ${method === m.k ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}>
                    <m.icon size={15} /> {m.label}
                  </button>
                ))}
              </div>

              {method === "otp" ? (
                <form onSubmit={handleOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="otp-email">Email</Label>
                    <Input id="otp-email" type="email" required value={email} disabled={otpStep === "verify"}
                      onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" data-testid="otp-login-email" />
                  </div>
                  {otpStep === "verify" && (
                    <div className="space-y-2">
                      <Label htmlFor="otp-code">6-digit code</Label>
                      <Input id="otp-code" required value={otpCode} maxLength={6}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="______" data-testid="otp-login-code"
                        className="text-center tracking-[0.4em] font-mono" />
                    </div>
                  )}
                  <Button type="submit" disabled={loading} data-testid="otp-login-submit"
                    className="w-full h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white font-medium transition-colors">
                    {loading ? "Please wait…" : otpStep === "request" ? "Send code" : "Verify & sign in"}
                  </Button>
                  {otpStep === "verify" && (
                    <button type="button" onClick={() => setOtpStep("request")} className="w-full text-xs text-emerald-700 hover:underline">Change email / resend</button>
                  )}
                  <p className="text-[11px] text-zinc-400 text-center">Sign in with a one-time code sent to your registered email.</p>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ident">{method === "mobile" ? "Mobile number" : "Email"}</Label>
                    {method === "mobile" ? (
                      <Input id="ident" required value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="9886260579" data-testid="login-mobile" />
                    ) : (
                      <Input id="ident" type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com" data-testid="login-email" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" data-testid="login-password" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="login-submit"
                    className="w-full h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white font-medium transition-colors">
                    {loading ? "Signing in…" : "Sign In"}
                    {!loading && <ArrowRight size={18} className="ml-1" />}
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rname">Full name</Label>
                  <Input id="rname" required value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Rahul Sharma" data-testid="register-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="remail">Email</Label>
                  <Input id="remail" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com" data-testid="register-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rmobile">Mobile number</Label>
                  <Input id="rmobile" type="tel" required value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="9886260579" data-testid="register-mobile" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rpassword">Password</Label>
                  <Input id="rpassword" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password" data-testid="register-password" />
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit"
                  className="w-full h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white font-medium transition-colors">
                  {loading ? "Creating…" : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 border-t border-zinc-200 pt-4 text-center">
            <a href="/ecard" data-testid="login-ecard-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline">
              <IdentificationCard size={16} weight="duotone" /> Download your e-health card (no login)
            </a>
          </div>

          <Copyright className="mt-8 text-center" />
        </motion.div>
      </div>
    </div>
  );
}
